import { useMemo, useState, useEffect } from 'react';
import { useNotifications } from './useNotifications';
import { useAdminNotifications } from './useAdminNotifications';
import { useAuth } from '@/contexts/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface CombinedNotification {
  id: string;
  title: string;
  message: string;
  notification_type: string;
  is_read: boolean;
  created_at: string;
  related_id?: string;
  metadata?: Record<string, any>;
  source: 'general' | 'admin';
  company_id?: string;
}

interface UseCombinedNotificationsReturn {
  notifications: CombinedNotification[];
  unreadCount: number;
  isLoading: boolean;
  markAsRead: (notificationId: string, source: 'general' | 'admin') => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

export const useCombinedNotifications = (): UseCombinedNotificationsReturn => {
  const { user, profile } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!user?.id) { setIsAdmin(false); return; }
    supabase
      .from('company_members')
      .select('role')
      .eq('user_id', user.id)
      .eq('role', 'admin')
      .eq('status', 'active')
      .maybeSingle()
      .then(({ data }) => setIsAdmin(!!data));
  }, [user?.id]);

  // General notifications for all users
  const {
    notifications: generalNotifications,
    unreadCount: generalUnreadCount,
    isLoading: generalLoading,
    markAsRead: markGeneralAsRead,
    markAllAsRead: markAllGeneralAsRead,
    refreshNotifications: refreshGeneral,
  } = useNotifications();

  // Admin notifications only for admins
  const {
    notifications: adminNotifications,
    unreadCount: adminUnreadCount,
    isLoading: adminLoading,
    markAsRead: markAdminAsRead,
    markAllAsRead: markAllAdminAsRead,
    refreshNotifications: refreshAdmin,
  } = useAdminNotifications();

  // Combine and sort notifications
  const combinedNotifications = useMemo(() => {
    const general: CombinedNotification[] = generalNotifications.map(n => ({
      ...n,
      source: 'general' as const,
    }));

    const admin: CombinedNotification[] = isAdmin 
      ? adminNotifications.map(n => ({
          ...n,
          source: 'admin' as const,
          metadata: n.company_id ? { company_id: n.company_id } : undefined,
        }))
      : [];

    return [...general, ...admin].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [generalNotifications, adminNotifications, isAdmin]);

  const markAsRead = async (notificationId: string, source: 'general' | 'admin') => {
    if (source === 'general') {
      await markGeneralAsRead(notificationId);
    } else {
      await markAdminAsRead(notificationId);
    }
  };

  const markAllAsRead = async () => {
    await Promise.all([
      markAllGeneralAsRead(),
      isAdmin ? markAllAdminAsRead() : Promise.resolve(),
    ]);
  };

  const refreshNotifications = async () => {
    await Promise.all([
      refreshGeneral(),
      isAdmin ? refreshAdmin() : Promise.resolve(),
    ]);
  };

  return {
    notifications: combinedNotifications,
    unreadCount: generalUnreadCount + (isAdmin ? adminUnreadCount : 0),
    isLoading: generalLoading || (isAdmin && adminLoading),
    markAsRead,
    markAllAsRead,
    refreshNotifications,
  };
};